import type { LetterTaskUser } from '../../../../../../Features/letterBox/api/types';
import React, { useState } from 'react';
import { Alert, Button, Modal, ModalBody, ModalHeader, ModalFooter } from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import UserAutocomplete, { UtenteSearch } from '../../../../../Autocomplete/UserAutocomplete';
import { get } from '../../../../../../hook/request';
import { getCategoriesByParent } from '../../../../../../Features/categorie/api/categorie.api';
import { parseMetaName } from '../../../../../../Helpers';

export type LetterTaskForm = {
    utenti: LetterTaskUser[];
    progettoID: number;
    descrizione: string;
    stato: number;
    tipo: number;
    priorita: number;
    scadenza: string;
    rapportino: boolean;
};

type Project = { ID: number; nome: string };
type Props = { onClose: () => void; onSubmit: (values: LetterTaskForm) => Promise<void>; subject: string };

export default function LetterTaskModal({ onClose, onSubmit, subject }: Props) {
    const [users, setUsers] = useState<UtenteSearch[]>([]);
    const [project, setProject] = useState('');
    const [description, setDescription] = useState(subject);
    const [status, setStatus] = useState('');
    const [type, setType] = useState('');
    const [priority, setPriority] = useState('1');
    const [deadline, setDeadline] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [rapp, setRapportino] = useState(false);

    const projects = useQuery({
        queryKey: ['letter-task-projects'],
        queryFn: async () => {
            const res = await get<Project[] | { data?: Project[] }>('/progetti/list');
            const data = res.data;
            return (Array.isArray(data) ? data : data.data ?? []).flat();
        },
    });
    const statuses = useQuery({ queryKey: ['categories', 669], queryFn: () => getCategoriesByParent(669) });
    const types = useQuery({ queryKey: ['categories', 672], queryFn: () => getCategoriesByParent(672) });
    const loading = projects.isPending || statuses.isPending || types.isPending;
    const loadError = projects.isError || statuses.isError || types.isError;

    const submit = async (event: React.FormEvent) => {
        event.preventDefault();
        if (saving || loading || loadError) return;
        if (!users.length || !description.trim()) {
            setError('Seleziona almeno un utente e inserisci la descrizione.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await onSubmit({ utenti: users.map(user => ({ ID: Number(user.ID), nominativo: `${user.nome} ${user.cognome}`.trim(), email: user.email ?? "" })), progettoID: Number(project), descrizione: description.trim(), stato: Number(status), tipo: Number(type), priorita: Number(priority), scadenza: deadline, rapportino: rapp });
            onClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Errore durante la creazione del task.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal isOpen toggle={saving ? undefined : onClose} backdrop={saving ? 'static' : true} keyboard={!saving} size="lg">
            <ModalHeader toggle={saving ? undefined : onClose}>Aggiungi task</ModalHeader>
            <form onSubmit={submit} className='w-100'>
                <ModalBody>
                    {loading && <p role="status">Caricamento progetti, stati e tipi...</p>}
                    {loadError && <Alert color="danger">Impossibile caricare progetti, stati o tipi. <Button color="link" onClick={() => { projects.refetch(); statuses.refetch(); types.refetch(); }}>Riprova</Button></Alert>}
                    {error && <Alert color="danger">{error}</Alert>}
                    <fieldset disabled={saving}>
                        <div className="mb-3">
                            <label className="form-label">Utenti</label>
                            <UserAutocomplete selectedUsers={users.map(user => ({ ID: user.ID, nominativo: `${user.nome} ${user.cognome}` }))} onAddUser={user => setUsers(current => current.some(item => item.ID === user.ID) ? current : [...current, user])} onRemoveUser={id => setUsers(current => current.filter(user => user.ID !== id))} />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="letter-task-project" className="form-label">Progetti</label>
                            <select id="letter-task-project" className="form-select" required value={project} onChange={event => setProject(event.target.value)}>
                                <option value="">Seleziona progetto</option>
                                {projects.data?.map(item => <option key={item.ID} value={item.ID}>{item.nome}</option>)}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label htmlFor="letter-task-description" className="form-label">Descrizione</label>
                            <textarea id="letter-task-description" className="form-control" required rows={4} value={description} onChange={event => setDescription(event.target.value)} />
                        </div>
                        <div className="row">
                            <div className="col-md-6 mb-6">
                                <label htmlFor="letter-task-status" className="form-label">Stato</label>
                                <select id="letter-task-status" className="form-select" required value={status} onChange={event => setStatus(event.target.value)}>
                                    <option value="">Seleziona stato</option>
                                    {statuses.data?.filter(item => (item.abilitato ?? 1) === 1).map(item => <option key={item.ID} value={item.ID}>{parseMetaName(item.meta, 'it') || item.nome || item.codice || item.ID}</option>)}
                                </select>
                            </div>

                             <div className="col-md-6 mb-3">
                                <label htmlFor="letter-task-type" className="form-label">Tipo</label>
                                <select id="letter-task-type" className="form-select" required value={type} onChange={event => setType(event.target.value)}>
                                    <option value="">-- Seleziona tipo --</option>
                                    {types.data?.filter(item => (item.abilitato ?? 1) === 1).slice().sort((a, b) => (a.ordine ?? 0) - (b.ordine ?? 0)).map(item => <option key={item.ID} value={item.ID}>{parseMetaName(item.meta, 'it') || item.nome || item.codice || item.ID}</option>)}
                                </select>
                            </div>
                            
                            <div className="col-md-6 mb-6">
                                <label htmlFor="letter-task-priority" className="form-label">Priorità</label>
                                <select id="letter-task-priority" className="form-select" value={priority} onChange={event => setPriority(event.target.value)}>
                                    <option value="1">Bassa</option><option value="2">Media</option><option value="3">Alta</option>
                                </select>
                            </div>
                            <div className="col-md-6 mb-6">
                                <label htmlFor="letter-task-deadline" className="form-label">Data Esecuzione</label>
                                <input id="letter-task-deadline" type="date" className="form-control" required value={deadline} onChange={event => setDeadline(event.target.value)} />
                            </div>

                            

                        </div>
                        <div className='row'>
                            <div className="col-md-4 mb-3">
                                  {/* Rapportino */}
                                <div className="mb-3 form-check">
                                    <input
                                    type="checkbox"
                                    className="form-check-input"
                                    id="rapportinoCheck"
                                    checked={rapp}
                                    onChange={event => setRapportino(!rapp)}
                                    />
                                    <label className="form-check-label" htmlFor="rapportinoCheck">
                                    Rapportino
                                    </label>
                                </div>
                            </div>
                        </div>
                    </fieldset>
                </ModalBody>
                <ModalFooter>
                    <Button type="button" color="secondary" disabled={saving} onClick={onClose}>Annulla</Button>
                    <Button type="submit" color="primary" disabled={saving || loading || loadError}>{saving ? 'Aggiunta in corso...' : 'Aggiungi task'}</Button>
                </ModalFooter>
            </form>
        </Modal>
    );
}